import { RoleType } from '@bmwe/data/kwg/graphql';
import { ObjectType, ObjectTypes } from '../types/objectTypes';

export interface PermissionSetBase {
  allowedRoles: RoleType | RoleType[];
}

export interface PermissionSet extends PermissionSetBase {
  objectType?: string;
  dossierId?: string;
}

export interface PermissionDeclaration {
  objectType?: ObjectType;
  READ?: PermissionSetBase;
  EDIT?: PermissionSetBase;
  CONTRIBUTE?: PermissionSetBase;
  MANAGE?: PermissionSetBase;
}

// ============================================================================
// Global Permission Declarations
// ============================================================================

export const BASE_DATA_PERMISSIONS = {
  READ: {
    allowedRoles: RoleType.Reader,
  },
  EDIT: {
    allowedRoles: RoleType.Editor,
  },
  MANAGE: {
    allowedRoles: RoleType.Administrator,
  },
} as const satisfies PermissionDeclaration;

export const DOSSIER_PERMISSIONS = {
  objectType: ObjectTypes.DOSSIER,
  READ: {
    allowedRoles: RoleType.Reader,
  },
  CONTRIBUTE: {
    allowedRoles: RoleType.Contributor,
  },
  EDIT: {
    allowedRoles: RoleType.Editor,
  },
  MANAGE: {
    allowedRoles: RoleType.Administrator,
  },
} as const satisfies PermissionDeclaration;

/**
 * User management permissions
 * Editing users includes assigning roles, so it is restricted to administrators
 * (an editor could otherwise escalate any account to administrator).
 */
export const USER_MANAGEMENT_PERMISSIONS = {
  READ: {
    allowedRoles: RoleType.Reader,
  },
  EDIT: {
    allowedRoles: RoleType.Administrator,
  },
} as const satisfies PermissionDeclaration;

/**
 * Migration inconsistency dashboard: raw SharePoint migration errors across all
 * dossiers, so it is restricted to administrators.
 */
export const MIGRATION_INCONSISTENCY_PERMISSIONS = {
  READ: {
    allowedRoles: RoleType.Administrator,
  },
} as const satisfies PermissionDeclaration;

/**
 * Group management permissions
 * Editing groups includes assigning roles, so it is restricted to administrators
 * (an editor could otherwise escalate any group to administrator).
 */
export const GROUP_MANAGEMENT_PERMISSIONS = {
  READ: {
    allowedRoles: RoleType.Reader,
  },
  EDIT: {
    allowedRoles: RoleType.Administrator,
  },
} as const satisfies PermissionDeclaration;

/**
 * Document template permissions (Stammdaten > Vorlagen).
 * Templates are regular master data, editable by editors — unlike user/group
 * management, whose EDIT level is restricted to administrators.
 */
export const TEMPLATES_PERMISSIONS = {
  READ: {
    allowedRoles: RoleType.Reader,
  },
  EDIT: {
    allowedRoles: RoleType.Editor,
  },
} as const satisfies PermissionDeclaration;

/**
 * Stammdaten (Master Data) permissions
 */
export const FEES_PERMISSIONS = {
  objectType: ObjectTypes.FEE,
  READ: {
    allowedRoles: RoleType.Reader,
  },
  EDIT: {
    allowedRoles: RoleType.Editor,
  },
  MANAGE: {
    allowedRoles: RoleType.Administrator,
  },
} as const satisfies PermissionDeclaration;

export const GOODS_PERMISSIONS = {
  objectType: ObjectTypes.GOOD,
  READ: {
    allowedRoles: RoleType.Reader,
  },
  EDIT: {
    allowedRoles: RoleType.Editor,
  },
  MANAGE: {
    allowedRoles: RoleType.Administrator,
  },
} as const satisfies PermissionDeclaration;

export const COMPANIES_PERMISSIONS = {
  objectType: ObjectTypes.COMPANY,
  READ: {
    allowedRoles: RoleType.Reader,
  },
  EDIT: {
    allowedRoles: RoleType.Editor,
  },
  MANAGE: {
    allowedRoles: RoleType.Administrator,
  },
} as const satisfies PermissionDeclaration;

export const COUNTRIES_PERMISSIONS = {
  objectType: ObjectTypes.COUNTRY,
  READ: {
    allowedRoles: RoleType.Reader,
  },
  EDIT: {
    allowedRoles: RoleType.Editor,
  },
  MANAGE: {
    allowedRoles: RoleType.Administrator,
  },
} as const satisfies PermissionDeclaration;

export const CONTACTS_PERMISSIONS = {
  objectType: ObjectTypes.CONTACT,
  READ: {
    allowedRoles: RoleType.Reader,
  },
  EDIT: {
    allowedRoles: RoleType.Editor,
  },
  MANAGE: {
    allowedRoles: RoleType.Administrator,
  },
} as const satisfies PermissionDeclaration;

export const FILE_NUMBERS_PERMISSIONS = {
  objectType: ObjectTypes.FILE_NUMBER,
  READ: {
    allowedRoles: RoleType.Reader,
  },
  EDIT: {
    allowedRoles: RoleType.Editor,
  },
  MANAGE: {
    allowedRoles: RoleType.Administrator,
  },
} as const satisfies PermissionDeclaration;

export const COLLATERAL_CLAUSES_PERMISSIONS = {
  objectType: ObjectTypes.COLLATERAL_CLAUSE,
  READ: {
    allowedRoles: RoleType.Reader,
  },
  EDIT: {
    allowedRoles: RoleType.Editor,
  },
  MANAGE: {
    allowedRoles: RoleType.Administrator,
  },
} as const satisfies PermissionDeclaration;

export const LOG_TEMPLATES_PERMISSIONS = {
  objectType: ObjectTypes.LOG_TEMPLATE,
  READ: {
    allowedRoles: RoleType.Reader,
  },
  EDIT: {
    allowedRoles: RoleType.Editor,
  },
  MANAGE: {
    allowedRoles: RoleType.Administrator,
  },
} as const satisfies PermissionDeclaration;

export const CHOICES_PERMISSIONS = {
  objectType: ObjectTypes.CHOICE,
  READ: {
    allowedRoles: RoleType.Reader,
  },
  EDIT: {
    allowedRoles: RoleType.Editor,
  },
} as const satisfies PermissionDeclaration;

/**
 * Helper to create a dossier-specific permission from a base permission
 */
export function forDossier(
  permission: PermissionSet,
  dossierId: string,
): PermissionSet {
  return {
    ...permission,
    dossierId,
  };
}

type PermissionKey<T extends PermissionDeclaration> = Exclude<
  keyof T,
  'objectType'
>;

export function getPermissionSet<
  T extends PermissionDeclaration,
  K extends PermissionKey<T>,
>(declaration: T & Record<K, PermissionSetBase>, set: K): PermissionSet {
  const permissionSet = declaration[set];

  return {
    ...permissionSet,
    objectType: declaration.objectType,
  };
}
